import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  CORE_ENTITY_TYPES,
  RELATION_TYPES,
  coreEntityMeta,
  createEntityRelation,
  deleteEntityRelation,
  fetchRelationStats,
  listAllEntityRelations,
  relationTypeMeta,
  type CoreEntityRelation,
} from '@/lib/coreRelations';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function RelationsTab({ org }: Props) {
  const [items, setItems] = useState<CoreEntityRelation[]>([]);
  const [stats, setStats] = useState<{ total: number; byPair: Record<string, number> }>({ total: 0, byPair: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sourceType: 'customer', sourceId: '', targetType: 'organization', targetId: '', relationType: 'belongs_to_company' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, st] = await Promise.all([listAllEntityRelations(org.id), fetchRelationStats(org.id)]);
      setItems(list);
      setStats(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.sourceId.trim() || !form.targetId.trim()) return;
    setSaving(true);
    try {
      await createEntityRelation({
        tenantId: org.tenant_id,
        organizationId: org.id,
        sourceType: form.sourceType,
        sourceId: form.sourceId.trim(),
        targetType: form.targetType,
        targetId: form.targetId.trim(),
        relationType: form.relationType,
      });
      setOpen(false);
      setForm({ ...form, sourceId: '', targetId: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: CoreEntityRelation) => {
    setBusyId(r.id);
    try { await deleteEntityRelation(r.id); load(); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Relations entre entités : un système cohérent et réutilisable pour relier n’importe quelles entités du socle
          (Client ↔ Entreprise, Service ↔ Professionnel, Réservation ↔ Client/Service…).
        </p>
        <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouvelle relation
        </button>
      </div>

      {Object.keys(stats.byPair).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(stats.byPair).slice(0, 8).map(([pair, count]) => (
            <div key={pair} className="rounded-lg border bg-background-50 border-background-200/70 p-3">
              <p className="text-xs font-medium text-foreground-700 truncate">{pair}</p>
              <p className="text-lg font-bold text-foreground-900 mt-1">{count}</p>
            </div>
          ))}
        </div>
      )}

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-links-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucune relation enregistrée.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const src = coreEntityMeta(r.source_type);
            const tgt = coreEntityMeta(r.target_type);
            const rel = relationTypeMeta(r.relation_type);
            return (
              <div key={r.id} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap">
                    <i className={src?.icon || 'ri-record-circle-line'}></i>{src?.label || r.source_type} #{r.source_id}
                  </span>
                  <i className="ri-arrow-right-line text-foreground-400 flex-shrink-0"></i>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">{rel.label}</span>
                  <i className="ri-arrow-right-line text-foreground-400 flex-shrink-0"></i>
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary-100 text-primary-900 whitespace-nowrap">
                    <i className={tgt?.icon || 'ri-record-circle-line'}></i>{tgt?.label || r.target_type} #{r.target_id}
                  </span>
                </div>
                <button onClick={() => remove(r)} disabled={busyId === r.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0"><i className="ri-delete-bin-line"></i></button>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title="Nouvelle relation"
          onClose={() => setOpen(false)}
          maxWidth="max-w-xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={submit} disabled={saving || !form.sourceId.trim() || !form.targetId.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Créer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Entité source</span>
                <select className={INPUT} value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })}>
                  {CORE_ENTITY_TYPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant source</span>
                <input className={INPUT} value={form.sourceId} onChange={(e) => setForm({ ...form, sourceId: e.target.value })} placeholder="Ex : 100234" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Type de relation</span>
              <select className={INPUT} value={form.relationType} onChange={(e) => setForm({ ...form, relationType: e.target.value })}>
                {RELATION_TYPES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Entité cible</span>
                <select className={INPUT} value={form.targetType} onChange={(e) => setForm({ ...form, targetType: e.target.value })}>
                  {CORE_ENTITY_TYPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant cible</span>
                <input className={INPUT} value={form.targetId} onChange={(e) => setForm({ ...form, targetId: e.target.value })} placeholder="Ex : 5" />
              </label>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}